/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import DS from 'ember-data';
import { CampaignsTriggerSetupFiltersArgs } from 'cio/components/campaigns/trigger/setup/condition-blocks/person/config';
import { task } from 'ember-concurrency';
import SegmentModel from 'cio/models/segment';
import { action } from '@ember/object';
import { CampaignRestartModes, CampaignType } from 'cio/models/campaign';

interface ConditionBlockPersonArgs extends CampaignsTriggerSetupFiltersArgs {
  columnCount: number;
  openSidebarOnMountEmpty?: boolean;
  onSidebarClose?: (hasConditions: boolean) => unknown;
}

export default class ConditionBlockPerson extends Component<ConditionBlockPersonArgs> {
  @service store!: DS.Store;

  segments: Record<string, SegmentModel> = {};
  segmentIdSet: Set<string> = new Set();

  constructor(owner: unknown, args: ConditionBlockPersonArgs) {
    super(owner, args);

    this.fetchSegments.perform(args.filters);
  }

  get highlight() {
    return this.segmentCount === 0;
  }

  get segmentNames() {
    let names: Record<string, string> = {};
    this.store
      .peekAll('short-segment')
      .toArray()
      .forEach((segment) => {
        names[segment.id] = segment.name;
      });
    return names;
  }

  get segmentCount() {
    return this.args.filters?.length ?? 0;
  }

  get cutThreshold() {
    let { maxSegmentCountBeforeThreshold } = this.args;
    // if more than threshold, then we show "threshold minus 1" segments and "x more"
    if (this.segmentCount > maxSegmentCountBeforeThreshold) {
      return maxSegmentCountBeforeThreshold - 1;
    }
    return maxSegmentCountBeforeThreshold;
  }

  get cutOffCount() {
    return this.segmentCount - this.cutThreshold;
  }

  get hasIncompatibleFrequency() {
    return this.args.restartMode !== CampaignRestartModes.None && this.args.type === CampaignType.Behavioral;
  }

  get disabledTooltip() {
    if (this.hasIncompatibleFrequency) {
      return 'Filters can only be added when a Trigger frequency is "one time." Update the frequency, and then you\'ll be able to add filters.';
    }
    return '';
  }

  // fetchSegments will only fetch those segments for which we need to display preview
  fetchSegments = task(async (filters: CampaignsTriggerSetupFiltersArgs['filters']) => {
    let anchorsToCheck = this.segmentCount > this.args.maxSegmentCountBeforeThreshold ? 2 : 3;

    filters?.slice(0, anchorsToCheck).forEach((filter) => {
      let segmentIds = (filter.segments as string[]) ?? [];
      if (segmentIds.length === 1) {
        this.segmentIdSet.add(segmentIds[0]);
      }
    });

    let segmentsToFetch = [];

    for (let segmentId of this.segmentIdSet) {
      if (this.segments[segmentId] == null) {
        if (this.store.peekRecord('segment', segmentId) == null) {
          segmentsToFetch.push(segmentId);
        } else {
          this.segments[segmentId] = this.store.peekRecord('segment', segmentId) as SegmentModel;
        }
      }
    }

    (await Promise.all(segmentsToFetch.map((segmentId) => this.store.findRecord('segment', segmentId)))).forEach(
      (segment) => {
        this.segments[segment.id] = segment;
      },
    );
  });

  @action
  updateFilters(conditions: Record<string, unknown>[]) {
    this.fetchSegments.perform(conditions);
    this.args.updateFilters(conditions);
  }

  @action
  onSidebarClose(sidebarWasVisible: boolean) {
    if (this.args.onSidebarClose != null && sidebarWasVisible === true && this.args.openSidebarOnMountEmpty === true) {
      this.args.onSidebarClose(!this.highlight);
    }
  }
}
